import { useCallback, useEffect, useState } from 'react';
import notify from '../../../utils/notify';
import useNegotiationApi from './useNegotiationApi';
import { normalizeDetail } from '../utils/negotiationStatus';

// `procurementId` is the route `:id` (GUID). Every action refetches the
// package afterwards so statuses, waitingFor and the timer come from the BE.
// Action errors are thrown to the caller, which owns the toast.
const useNegotiationDetail = (
  procurementId,
  { endpoint = '/negotiation' } = {}
) => {
  const api = useNegotiationApi();
  const { getDetail } = api;
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDetail = useCallback(
    async ({ silent = false } = {}) => {
      if (!procurementId) return;
      if (!silent) setIsLoading(true);
      try {
        // serverNow is stamped mid-request: use the round-trip midpoint.
        const t0 = Date.now();
        const data = await getDetail(procurementId, { endpoint });
        setDetail(normalizeDetail(data, (t0 + Date.now()) / 2));
      } catch (error) {
        if (!silent) {
          notify.apiError(error, 'load negotiation detail');
          setDetail(null);
        }
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [endpoint, getDetail, procurementId]
  );

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const withRefresh =
    fn =>
    async (...args) => {
      const result = await fn(...args);
      await fetchDetail({ silent: true });
      return result;
    };

  return {
    detail,
    isLoading,
    refresh: fetchDetail,
    startCargo: withRefresh(api.startCargo),
    extendCargo: withRefresh(api.extendCargo),
    submitRound: withRefresh(api.submitRound),
    acceptLine: withRefresh(api.acceptLine),
    withdrawRound: withRefresh(api.withdrawRound),
    withdrawPartner: withRefresh(api.withdrawPartner),
  };
};

export default useNegotiationDetail;
